/**
 * SeedRegistry — the house pattern (mirrors ConnectorRegistry /
 * DashboardSurfaceRegistry / DomainDataProfile). Domains register seed
 * providers; the editor's `useStartIntentSeed` looks one up by id and runs
 * its payload. Adding curated starter content becomes a data entry, not a
 * new mechanism. See docs/plans/2026-06-10-start-intent-and-seeding-plan.md.
 */

import type * as Y from 'yjs';
import type { CommandManager } from '../commands/CommandManager';
import type { RdmLayoutOptions } from '../../domains/rdm/services/RdmLayoutService';

export type NativeSeedType = 'genogram' | 'flowchart' | 'org' | 'network';

/** Extra context a `commands` builder may use. `doc` lets a seed write
 *  diagram settings / frames (org viewMode, network containers) — optional
 *  because RDM seeds and tests don't need it. `folderId` + `requestFn` let a
 *  seed hydrate from the folder's canonical records (the source-backed
 *  starter path) — a seed that needs them and doesn't get them seeds
 *  nothing rather than guessing. */
export interface SeedBuildContext {
  doc?: Y.Doc | null;
  dialectId?: string | null;
  folderId?: string | null;
  requestFn?: (input: string, init?: RequestInit) => Promise<Response>;
}

/**
 * The payload a provider resolves to. Three formats wrap the three existing
 * mechanisms so nothing is rewritten:
 *  - `rdm`      → DEFAULT for new content; hydrated via the production stack
 *                 (RdmService → ApplyRdmSceneCommand), same as rdm_apply / import.
 *  - `commands` → an imperative builder (network's hydrators, org's CSV import).
 *                 CONTRACT: `build` MUST mutate the canvas ONLY via
 *                 `cm.execute(command)` — never touch a store directly — so the
 *                 seed stays undoable (architecture-rules Tool Command Pattern).
 *  - `clone`    → a stored `isTemplate` diagram cloned SERVER-side at create
 *                 time (genogram's path); the client hook is a no-op for these.
 *
 * `layoutOptions` lets a seed tune ELK spacing/anchors (e.g. a wider
 * `nodeSpacing` so branch nodes don't crowd the spine). `anchorMode` defaults
 * to 'redistribute' in the runner regardless.
 */
export type SeedPayload =
  | { format: 'rdm'; rdm: string; layoutOptions?: RdmLayoutOptions }
  | { format: 'commands'; build: (cm: CommandManager, ctx?: SeedBuildContext) => Promise<void> | void }
  | { format: 'clone'; templateDiagramId: string };

export interface SeedProvider {
  /** Stable id referenced by `seed=sample:<id>` (e.g. 'flowchart-basic'). */
  id: string;
  /** Native diagram type this seed targets. */
  type: NativeSeedType;
  /** Human label (analytics / future picker). */
  label: string;
  /** True when the seed hydrates from folder records and must not run until
   *  `SeedBuildContext.folderId` + `requestFn` are available — the runner
   *  waits instead of claiming the one-shot with missing context. */
  needsFolderContext?: boolean;
  /** Resolve the payload. Lazy — heavy content stays out of the initial bundle. */
  provide: () => Promise<SeedPayload> | SeedPayload;
}

class Registry {
  private readonly byId = new Map<string, SeedProvider>();

  register(provider: SeedProvider): void {
    // Last registration wins (idempotent re-registration is harmless).
    this.byId.set(provider.id, provider);
  }

  get(id: string): SeedProvider | null {
    return this.byId.get(id) ?? null;
  }

  list(): SeedProvider[] {
    return Array.from(this.byId.values());
  }

  /** Test-only. */
  __reset(): void {
    this.byId.clear();
  }
}

export const seedRegistry = new Registry();

export function registerSeedProvider(provider: SeedProvider): void {
  seedRegistry.register(provider);
}

export function getSeedProvider(id: string): SeedProvider | null {
  return seedRegistry.get(id);
}
